/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import Clarity from '@microsoft/clarity';
import { useEffect, useRef } from 'react';

declare global {
    interface Window {
        cookieconsent: any;
        clarity?: (...args: any[]) => void;
        clarityInitialized?: boolean;
        __APP_USER?: { id?: string };
    }
}

const COOKIE_SCRIPT_SRC =
    'https://cdn.jsdelivr.net/npm/cookieconsent@3/build/cookieconsent.min.js';
const COOKIE_CSS =
    'https://cdn.jsdelivr.net/npm/cookieconsent@3/build/cookieconsent.min.css';

const CLARITY_PROJECT_ID = 'q22qbtzh13';

function appendScript(src: string, id: string) {
    if (document.getElementById(id)) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(`Failed to load script: ${src}`);
        document.head.appendChild(script);
    });
}

function appendCss(href: string) {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
}

export default function ClarityCookieConsent({ email }: { email?: string }) {
    const clarityStarted = useRef(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (process.env.NEXT_PUBLIC_ENVIRONMENT_MODE !== 'production') return;

        appendCss(COOKIE_CSS);

        appendScript(COOKIE_SCRIPT_SRC, 'cookieconsent-script')
            .then(() => initCookieConsent())
            .catch(err => console.warn(err));
    }, []);

    useEffect(() => {
        if (process.env.NEXT_PUBLIC_ENVIRONMENT_MODE !== 'production') return;
        if (clarityStarted.current && email && window.clarity) {
            Clarity.setTag('email', email);
        }
    }, [email]);

    const hideConsentUI = () => {
        const popup = document.querySelector('.cc-window') as HTMLElement;
        const revokeBtn = document.querySelector('.cc-revoke') as HTMLElement;

        if (popup) popup.style.display = 'none';
        if (revokeBtn) revokeBtn.remove();

        setTimeout(() => {
            const r = document.querySelector('.cc-revoke') as HTMLElement;
            if (r) r.remove();
        }, 100);
    };

    const initializeClarity = () => {
        if (clarityStarted.current) return;
        clarityStarted.current = true;

        Clarity.init(CLARITY_PROJECT_ID);

        window.clarity?.('consent', 'update', {
            ad_storage: 'denied',
            analytics_storage: 'granted'
        });

        Clarity.setTag('portal', 'organization');

        if (email && window.clarity) {
            Clarity.setTag('email', email);
        }
    };

    const initCookieConsent = () => {
        if (!window.cookieconsent?.initialise) return;

        window.cookieconsent.initialise({
            palette: {
                popup: { background: '#ffffff', text: '#1f2937' },
                button: { background: 'rgb(136, 90, 173)', text: '#ffffff' }
            },
            theme: 'classic',
            position: 'bottom',
            type: 'opt-in',
            revokable: false,
            content: {
                message:
                    'We use cookies for analytics (Microsoft Clarity) to improve your experience. We do not collect personal information.',
                allow: 'Accept',
                deny: 'Decline',
                link: 'Privacy Policy',
                href: 'https://jobs.abroadworks.com/privacy-policy',
                target: '_blank'
            },

            onInitialise: (status: string) => {
                hideConsentUI();
                if (status === 'allow') initializeClarity();
            },

            onStatusChange: (status: string) => {
                hideConsentUI();
                if (status === 'allow') initializeClarity();
            }
        });
    };

    return null;
}
